import { Content, Header, Page } from '@backstage/core-components';
import {
  Grid,
  Card,
  CardActionArea,
  CardContent,
  Typography,
  Box,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useState } from 'react';
import { Link } from 'react-router-dom';

const useStyles = makeStyles(theme => ({
  container: {
    margin: theme.spacing(5, 0),
  },
  sectionTitle: {
    marginBottom: theme.spacing(3),
    fontWeight: 600,
    letterSpacing: '0.5px',
  },
  card: {
    height: '100%',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    border: `1px solid ${theme.palette.divider}`,
    '&:hover': {
      transform: 'translateY(-8px)',
      boxShadow: theme.shadows[12],
      borderColor: theme.palette.primary.main,
    },
  },
  cardContent: {
    padding: theme.spacing(3),
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    height: '100%',
  },
  cardTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    color: theme.palette.text.primary,
  },
  cardDescription: {
    fontSize: '0.95rem',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
    flexGrow: 1,
  },
  cardIcon: {
    fontSize: '2.5rem',
    marginBottom: theme.spacing(2),
    color: theme.palette.primary.main,
  },
  actionLabel: {
    marginTop: theme.spacing(1),
    fontSize: '0.85rem',
    fontWeight: 500,
    color: theme.palette.primary.main,
    textTransform: 'uppercase',
    letterSpacing: '1px',
  },
}));

interface ToolCard {
  label: string;
  description: string;
  url: string;
  icon: string;
}

const ToolCard = ({
  tool,
  classes,
}: {
  tool: ToolCard;
  classes: Record<string, string>;
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const getIconEmoji = (icon: string) => {
    const icons: Record<string, string> = {
      catalog: '📚',
      api: '🔌',
      template: '🎨',
      docs: '📖',
      products: '📦',
      clzscale: '📋',
    };
    return icons[icon] || '🔗';
  };

  return (
    <Card
      className={classes.card}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <CardActionArea component={Link} to={tool.url} style={{ height: '100%' }}>
        <CardContent className={classes.cardContent}>
          <div className={classes.cardIcon}>{getIconEmoji(tool.icon)}</div>
          <Typography variant="h6" className={classes.cardTitle}>
            {tool.label}
          </Typography>
          {tool.description && (
            <Typography className={classes.cardDescription}>
              {tool.description}
            </Typography>
          )}
          <Typography className={classes.actionLabel}>
            {isHovered ? '詳細を見る →' : '詳細'}
          </Typography>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

export const HomePage = () => {
  const classes = useStyles();

  const tools: ToolCard[] = [
    {
      label: 'Catalog',
      description: '',
      url: '/catalog',
      icon: 'catalog',
    },
    {
      label: 'Docs',
      description: '',
      url: '/docs',
      icon: 'docs',
    },
    {
      label: 'Template',
      description: '',
      url: '/catalog?filters[kind]=template',
      icon: 'template',
    },
    {
      label: 'APIs',
      description: '',
      url: '/catalog?filters[kind]=api',
      icon: 'api',
    },
    {
      label: 'Products',
      description: '',
      url: '/idp-control-base/product-list',
      icon: 'products',
    },
    {
      label: 'clz.scale Use Request',
      description: '',
      url: '/idp-control-base/tenant-join-request',
      icon: 'clzscale',
    },
  ];

  return (
    <Page themeId="home">
      <Header title="Internal Developer Portal" />
      <Content>
        <Box className={classes.container}>
          <Grid container spacing={3}>
            {tools.map((tool, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <ToolCard tool={tool} classes={classes} />
              </Grid>
            ))}
          </Grid>
        </Box>
      </Content>
    </Page>
  );
};
